import React from 'react';
import './Explainability.css';

/**
 * CitationPanel
 *
 * Renders the surviving citation_index from the SNN pipeline as a
 * numbered, clickable list. Each entry is anchored to a [N] superscript
 * inside the answer panel via shared `highlightedCite` state.
 *
 * Props:
 *  - citations:        Array of {cite_num, label, source_doc, pages, reference_text, node_id}
 *  - highlightedCite:  cite_num that should appear visually selected
 *  - onCiteClick:      (citeNum) => void  — caller scrolls answer panel + sets highlight
 */
const CitationPanel = ({ citations = [], highlightedCite = null, onCiteClick }) => {
  if (!citations.length) {
    return (
      <div className="explain-citation-panel">
        <h4 className="explain-citation-panel-title">Source citations</h4>
        <p className="explain-citation-panel-empty">
          No source citations are attached to this evaluation.
        </p>
      </div>
    );
  }

  return (
    <div className="explain-citation-panel">
      <h4 className="explain-citation-panel-title">
        Source citations ({citations.length})
      </h4>
      <ol className="explain-citation-list">
        {citations.map((c) => {
          const isActive = highlightedCite === c.cite_num;
          const pages = (c.pages || []).filter((p) => p);
          return (
            <li
              key={`cite-${c.cite_num}`}
              className={
                'explain-citation-item' +
                (isActive ? ' explain-citation-item--active' : '')
              }
              onClick={() => onCiteClick && onCiteClick(c.cite_num)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCiteClick && onCiteClick(c.cite_num);
                }
              }}
            >
              <div className="explain-citation-item-header">
                <span className="explain-citation-num">[{c.cite_num}]</span>
                <span className="explain-citation-label">{c.label || c.node_id}</span>
              </div>
              {(c.source_doc || pages.length > 0) && (
                <div className="explain-citation-source">
                  {c.source_doc && <span className="explain-citation-doc">{c.source_doc}</span>}
                  {pages.length > 0 && (
                    <span className="explain-citation-pages">
                      {' '}p.{pages.join(', ')}
                    </span>
                  )}
                </div>
              )}
              {c.reference_text && (
                <blockquote className="explain-citation-quote">
                  {c.reference_text}
                </blockquote>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default CitationPanel;
